import { add, differenceInMinutes, isBefore, parse } from 'date-fns';

import {
  BRANCH_TYPE,
  BRANCH_TYPE_TO_GERMAN_NAME,
  EINTRAGUNG_ALS_ID,
  EIV_ENTRY_TYPE,
  EIV_STATUS,
  EIV_STATUS_ALLOW_EXPERT_EDITING,
  EIV_STATUS_ALLOW_RENDER,
  INSTALLER_ENTRIES,
  ONEEIGHTY_DAYS_IN_MINUTES,
  TENANT_ID,
} from 'global/constants';

import { branchToCapital } from 'utils/DataUtils';

import { getTodayMidnightDate, parseEIVDateStringToEfixDateString } from './DatesUtils';
import { getTenantId, isBUofCurrentTenant, isGridnetProviderOfCurrentTenant } from './tenantUtil';

export const isFachinteressent = (installer) => {
  if (!installer) {
    return false;
  }

  const energyEntryTypeId = Number(installer.bEintragungAls?.id);
  const gasEntryTypeId = Number(installer.bEintragungGasAls?.id);

  return (
    energyEntryTypeId === EINTRAGUNG_ALS_ID.FACHINTERESSENT ||
    gasEntryTypeId === EINTRAGUNG_ALS_ID.FACHINTERESSENT
  );
};

export const getFachinteressentTaetigkeitsschwerpunkt = (installer) =>
  installer?.taetigkeitsschwerpunkt ||
  installer?.bBetriebsartStrom?.name ||
  installer?.bBetriebsartGas?.name ||
  '';

export const getEntryType = (installerEntry) => {
  if (!installerEntry) {
    return 'none';
  }

  if (installerEntry === EIV_ENTRY_TYPE.MAIN) {
    return 'main';
  }

  if (installerEntry === EIV_ENTRY_TYPE.GUEST) {
    return 'guest';
  }
  return 'none';
};
/**
 * return one of the following:
 * [mainEnergyOnly,
 * mainGasOnly,
 * guestOnly,
 * mainBoth,
 * mainEnergyGuestGas,
 * guestEnergyMainGas]
 * based on the values of enrgyEntry and gasEntry that can be 'main', 'guest' or 'none'
 *
 * @param {*} energyEntry
 * @param {*} gasEntry
 * @param {*} statusEnergy
 * @param {*} statusGas
 * @returns {*}
 */
const getInstallerEntry = (energyEntry, gasEntry, statusEnergy, statusGas) => {
  const allowedStatuses = EIV_STATUS_ALLOW_RENDER;
  let energyType = '';
  let gasType = '';
  if (allowedStatuses.includes(statusEnergy)) {
    energyType = energyEntry;
  }
  if (allowedStatuses.includes(statusGas)) {
    gasType = gasEntry;
  }

  if (energyType === 'main' && gasType === 'main') {
    return INSTALLER_ENTRIES.MAIN_BOTH;
  }
  if (energyType === 'main' && gasType === 'guest') {
    return INSTALLER_ENTRIES.MAIN_ENERGY_GUEST_GAS;
  }
  if (energyType === 'guest' && gasType === 'main') {
    return INSTALLER_ENTRIES.GUEST_ENERGY_MAIN_GAS;
  }
  if (energyType === 'main' && gasType === '') {
    return INSTALLER_ENTRIES.MAIN_ENERGY_ONLY;
  }
  if (energyType === '' && gasType === 'main') {
    return INSTALLER_ENTRIES.MAIN_GAS_ONLY;
  }
  if (energyType === 'guest' || gasType === 'guest') {
    return INSTALLER_ENTRIES.GUEST_ONLY;
  }
  return INSTALLER_ENTRIES.NONE;
};
export const canExtendBranch = (branch, branchExpireDate) => {
  return (
    differenceInMinutes(
      parse(branchExpireDate, 'dd.MM.yyyy', new Date()),
      getTodayMidnightDate()
    ) <= ONEEIGHTY_DAYS_IN_MINUTES
  );
};

/**
 * Returns true when the given expert's branch has already been extended —
 * i.e. the validity date changed or the isExpertExtended* flag was set.
 */
export const isExpertBranchExtended = (expert, oldExpert, branch) => {
  if (!oldExpert) return false;
  if (branch === BRANCH_TYPE.ENERGY) {
    return (
      expert.expertEnergyValidityDate !== oldExpert.expertEnergyValidityDate ||
      expert.isExpertExtendedEnergy === true
    );
  }
  return (
    expert.expertGasValidityDate !== oldExpert.expertGasValidityDate ||
    expert.isExpertExtendedGas === true
  );
};

export const doesRegisteredInstallerHaveBranch = (branch, installerStatusSummary) =>
  installerStatusSummary?.[branch]?.entryType !== 'none' &&
  EIV_STATUS_ALLOW_RENDER.includes(installerStatusSummary?.[branch]?.status);
export const isRegisteredBranchActive = (installerStatusSummary, branch) =>
  installerStatusSummary[branch].status === EIV_STATUS.ACTIVE;
export const isRegisteredBranchGuest = (installerStatusSummary, branch) =>
  installerStatusSummary[branch].entryType === 'guest' &&
  EIV_STATUS_ALLOW_RENDER.includes(installerStatusSummary[branch].status);

export const canBranchRenderByStatus = (installerStatusSummary, branch) =>
  EIV_STATUS_ALLOW_RENDER.includes(installerStatusSummary[branch].status);

export const hasGuestBranchExtended = (experts, initialExperts, branch) => {
  let hasExtended;
  if (branch === BRANCH_TYPE.ENERGY) {
    hasExtended = experts.some((expert, index) => {
      if (
        initialExperts[index] &&
        expert.expertEnergyValidityDate !== initialExperts[index].expertEnergyValidityDate
      ) {
        return true;
      }
      return false;
    });
  }
  if (branch === BRANCH_TYPE.GAS) {
    hasExtended = experts.some((expert, index) => {
      if (
        initialExperts[index] &&
        expert.expertGasValidityDate !== initialExperts[index].expertGasValidityDate
      ) {
        return true;
      }
      return false;
    });
  }

  return hasExtended;
};

export const getBranchStatusAndExpireDate = (installer, branchType) => {
  const branchTypeVar = branchType === BRANCH_TYPE.ENERGY ? 'Strom' : 'Gas';

  return [installer[`status${branchTypeVar}`], installer[`ausw${branchTypeVar}Gueltig`]];
};
/**
 * Description placeholder
 *
 * @param {*} installer
 * @returns {{ [x: BRANCH_TYPE]: { status: any; entryType: string; companyType: any; expireDate: any; }; installerEntry: any; }}
 */
export const getInstallerStatusSummary = (installer) => {
  if (!installer || typeof installer !== 'object' || Object.keys(installer).length === 0) {
    throw new Error('Installer data is required');
  }
  const [energyStatus, energyExpireDate] = getBranchStatusAndExpireDate(
    installer,
    BRANCH_TYPE.ENERGY
  );
  const [gasStatus, gasExpireDate] = getBranchStatusAndExpireDate(installer, BRANCH_TYPE.GAS);

  const entryTypeEnergy = getEntryType(installer.eintragungsartStrom);
  const entryTypeGas = getEntryType(installer.eintragungsartGas);
  const installerEntry = getInstallerEntry(entryTypeEnergy, entryTypeGas, energyStatus, gasStatus);
  const getCompanyTypeEnergy = (branch) => {
    if (energyStatus === EIV_STATUS.NOT_SET) {
      return null;
    }
    return installer.bBetriebsartStrom?.name;
  };
  const getCompanyTypeGas = (branch) => {
    if (gasStatus === EIV_STATUS.NOT_SET) {
      return null;
    }
    return installer.bBetriebsartGas?.name;
  };
  return {
    [BRANCH_TYPE.ENERGY]: {
      status: energyStatus,
      entryType: getEntryType(installer.eintragungsartStrom),
      companyType: getCompanyTypeEnergy(BRANCH_TYPE.ENERGY),
      expireDate: parseEIVDateStringToEfixDateString(energyExpireDate),
    },
    [BRANCH_TYPE.GAS]: {
      status: gasStatus,
      entryType: getEntryType(installer.eintragungsartGas),
      companyType: getCompanyTypeGas(BRANCH_TYPE.GAS),
      expireDate: parseEIVDateStringToEfixDateString(gasExpireDate),
    },
    installerEntry,
    pflegendeBu: installer.pflegendeBu,
    betriebsartStrom: installer.bBetriebsartStrom?.name,
    betriebsartGas: installer.bBetriebsartGas?.name,
  };
};
export const getInstallerStatusbarData = (installer) => {
  let energyEntry;
  let gasEntry;
  let limitationEntry;

  const installerStatusSummary = getInstallerStatusSummary(installer);

  const hasEnergyRow = doesRegisteredInstallerHaveBranch(
    BRANCH_TYPE.ENERGY,
    installerStatusSummary
  );

  const hasGasRow = doesRegisteredInstallerHaveBranch(BRANCH_TYPE.GAS, installerStatusSummary);

  if (hasEnergyRow) {
    energyEntry = {
      ...installerStatusSummary[BRANCH_TYPE.ENERGY],
      shouldExtend: isBefore(
        parse(installerStatusSummary[BRANCH_TYPE.ENERGY].expireDate, 'dd.MM.yyyy', new Date()),
        add(new Date(), { days: 30 })
      ),
      branch: BRANCH_TYPE_TO_GERMAN_NAME[BRANCH_TYPE.ENERGY],
    };
  }

  if (hasGasRow) {
    gasEntry = {
      ...installerStatusSummary[BRANCH_TYPE.GAS],
      shouldExtend: isBefore(
        parse(installerStatusSummary[BRANCH_TYPE.GAS].expireDate, 'dd.MM.yyyy', new Date()),
        add(new Date(), { days: 30 })
      ),
      branch: BRANCH_TYPE_TO_GERMAN_NAME[BRANCH_TYPE.GAS],
    };
  }

  let companyName = installer.firmenname2
    ? `${installer.firmenname1} ${installer.firmenname2}`
    : installer.firmenname1;
  if (getTenantId() === TENANT_ID.BAG) {
    limitationEntry = {
      energy: hasEnergyRow ? installer.eintragungsartErgStrom : undefined,
      gas: hasGasRow ? installer.eintragungsartErgGas : undefined,
    };
    if (installer.firmenname3) {
      companyName += ` ${installer.firmenname3}`;
    }
  }

  return {
    id: installer.id,
    name: companyName,
    energyEntry,
    gasEntry,
    limitationEntry,
    isFachinteressent: isFachinteressent(installer),
    taetigkeitsschwerpunkt: getFachinteressentTaetigkeitsschwerpunkt(installer),
  };
};

export const hasMainEntry = (installerData) => {
  const {
    pflegendeBu,
    bNetzbetreiberStrom,
    bNetzbetreiberGas,
    eintragungsartStrom,
    eintragungsartGas,
  } = installerData;

  if (!isBUofCurrentTenant(pflegendeBu)) {
    return false;
  }
  const energyEntryType = getEntryType(eintragungsartStrom);
  const gasEntryType = getEntryType(eintragungsartGas);

  if (energyEntryType !== 'main' && gasEntryType !== 'main') {
    return false;
  }
  // SHNG and EDIS are skipped due to changing list of grid providers
  if ([TENANT_ID.SHNG, TENANT_ID.EDI].includes(getTenantId())) {
    return true;
  }
  if (energyEntryType === 'main' && isGridnetProviderOfCurrentTenant(bNetzbetreiberStrom.id)) {
    return true;
  }
  if (gasEntryType === 'main' && isGridnetProviderOfCurrentTenant(bNetzbetreiberGas.id)) {
    return true;
  }

  return false;
};

export const hasDownloadEntry = (installerData) => {
  const { pflegendeBu, eintragungsartStrom, eintragungsartGas } = installerData;

  if (!isBUofCurrentTenant(pflegendeBu)) {
    return false;
  }

  const energyEntryType = getEntryType(eintragungsartStrom);
  const gasEntryType = getEntryType(eintragungsartGas);

  const [energyStatus] = getBranchStatusAndExpireDate(installerData, BRANCH_TYPE.ENERGY);
  const [gasStatus] = getBranchStatusAndExpireDate(installerData, BRANCH_TYPE.GAS);

  // checking if both of them are guest to block the download tile showing as SHNG only want main entries downloads for now
  if (getTenantId() === TENANT_ID.SHNG) {
    if (
      (energyEntryType === 'guest' && gasEntryType === 'guest') ||
      (energyEntryType === 'guest' && gasEntryType === 'none') ||
      (energyEntryType === 'none' && gasEntryType === 'guest')
    ) {
      return false;
    }
    if (
      energyEntryType === 'main' &&
      gasEntryType !== 'main' &&
      energyStatus !== EIV_STATUS.ACTIVE
    ) {
      return false;
    }
    if (gasEntryType === 'main' && energyEntryType !== 'main' && gasStatus !== EIV_STATUS.ACTIVE) {
      return false;
    }
  }

  return true;
};

export const hasGuestEntry = (installerData) => {
  const { pflegendeBu, eintragungsartStrom, eintragungsartGas } = installerData;
  if (!isBUofCurrentTenant(pflegendeBu)) {
    return false;
  }

  const energyEntryType = getEntryType(eintragungsartStrom);
  const gasEntryType = getEntryType(eintragungsartGas);

  if (energyEntryType !== 'guest' && gasEntryType !== 'guest') {
    return false;
  }

  return true;
};

export const isInstallerRegisteringForBranch = (branch, branchSelectionValues) =>
  branchSelectionValues[`is${branchToCapital(branch)}Active`];

export const isRegisteringBranchGuest = (branch, branchSelectionValues) =>
  branchSelectionValues[`branchType${branchToCapital(branch)}`] === EIV_ENTRY_TYPE.GUEST;

export const isRegisteringBranchMain = (branch, branchSelectionValues) =>
  branchSelectionValues[`branchType${branchToCapital(branch)}`] === EIV_ENTRY_TYPE.MAIN;

export const isInstallerRegisteringOnlyForBranch = (branch, branchSelectionValues) => {
  const otherBranch = branch === BRANCH_TYPE.ENERGY ? BRANCH_TYPE.GAS : BRANCH_TYPE.ENERGY;
  return (
    isInstallerRegisteringForBranch(branch, branchSelectionValues) &&
    !isInstallerRegisteringForBranch(otherBranch, branchSelectionValues)
  );
};
export const isRegisteringInstallerOnlyHilfsbetrieb = (installer) => {
  const isEnergyHilfsbetrieb = installer.companyTypeEnergy === 'Hilfsbetrieb';
  const isGasHilfsbetrieb = installer.companyTypeGas === 'Hilfsbetrieb';
  const hasEnergyBranch = isInstallerRegisteringForBranch(BRANCH_TYPE.ENERGY, installer);
  const hasGasBranch = isInstallerRegisteringForBranch(BRANCH_TYPE.GAS, installer);

  const isEnergyMain = !isRegisteringBranchGuest(BRANCH_TYPE.ENERGY, installer);
  const isGasMain = !isRegisteringBranchGuest(BRANCH_TYPE.GAS, installer);
  if (hasEnergyBranch && hasGasBranch) {
    if (isEnergyMain && isGasMain) {
      // Both must be 'Hilfsbetrieb' to return true
      return isEnergyHilfsbetrieb && isGasHilfsbetrieb;
    }
    // At least one main branch must be 'Hilfsbetrieb' to return true
    return (isEnergyMain && isEnergyHilfsbetrieb) || (isGasMain && isGasHilfsbetrieb);
  }
  // Case: Only one branch exists
  // The existing branch must be 'Hilfsbetrieb' to return true
  return (hasEnergyBranch && isEnergyHilfsbetrieb) || (hasGasBranch && isGasHilfsbetrieb);
};
export const isRegisteringInstallerGuestOnly = (branchSelectionValues) =>
  (isInstallerRegisteringForBranch(BRANCH_TYPE.ENERGY, branchSelectionValues)
    ? isRegisteringBranchGuest(BRANCH_TYPE.ENERGY, branchSelectionValues)
    : true) &&
  (isInstallerRegisteringForBranch(BRANCH_TYPE.GAS, branchSelectionValues)
    ? isRegisteringBranchGuest(BRANCH_TYPE.GAS, branchSelectionValues)
    : true);

export const isRegisteredInstallerGuestOnly = (installerStatusSummary) =>
  (doesRegisteredInstallerHaveBranch(BRANCH_TYPE.ENERGY, installerStatusSummary)
    ? isRegisteredBranchGuest(installerStatusSummary, BRANCH_TYPE.ENERGY)
    : true) &&
  (doesRegisteredInstallerHaveBranch(BRANCH_TYPE.GAS, installerStatusSummary)
    ? isRegisteredBranchGuest(installerStatusSummary, BRANCH_TYPE.GAS)
    : true);

export const isRegisteredInstallerBranchHilfsbetrieb = (installerStatusSummary, branchType) =>
  installerStatusSummary[branchType].companyType === 'Hilfsbetrieb';

export const isRegisteredBranchMain = (installerStatusSummary, branchType) =>
  doesRegisteredInstallerHaveBranch(branchType, installerStatusSummary) &&
  !isRegisteredBranchGuest(installerStatusSummary, branchType);

/**
 * Returns true when the given expert still has a visible, unclicked "Verlängern" button
 * for at least one *Stamm* branch — i.e. the same condition under which extensionButton() in
 * ExpertTableRows renders the button for main entries: isRegisteredBranchMain AND
 * canExtendBranch AND status allows editing AND the branch has not yet been extended.
 */
export const expertHasUnextendedBranch = (expert, oldExpert, installerStatusSummary) =>
  (!isExpertBranchExtended(expert, oldExpert, BRANCH_TYPE.ENERGY) &&
    isRegisteredBranchMain(installerStatusSummary, BRANCH_TYPE.ENERGY) &&
    canExtendBranch(BRANCH_TYPE.ENERGY, expert.expertEnergyValidityDate) &&
    EIV_STATUS_ALLOW_EXPERT_EDITING.includes(expert.statusEnergy)) ||
  (!isExpertBranchExtended(expert, oldExpert, BRANCH_TYPE.GAS) &&
    isRegisteredBranchMain(installerStatusSummary, BRANCH_TYPE.GAS) &&
    canExtendBranch(BRANCH_TYPE.GAS, expert.expertGasValidityDate) &&
    EIV_STATUS_ALLOW_EXPERT_EDITING.includes(expert.statusGas));

export const isRegisteredInstallerOnlyMain = (installerStatusSummary) => {
  const isEnergyMain = isRegisteredBranchMain(installerStatusSummary, BRANCH_TYPE.ENERGY);
  const isGasMain = isRegisteredBranchMain(installerStatusSummary, BRANCH_TYPE.GAS);

  return (
    (isEnergyMain && !doesRegisteredInstallerHaveBranch(BRANCH_TYPE.GAS, installerStatusSummary)) ||
    (isGasMain && !doesRegisteredInstallerHaveBranch(BRANCH_TYPE.ENERGY, installerStatusSummary)) ||
    (isEnergyMain && isGasMain)
  );
};
export const hasRegisteredInstallerOneMain = (installerStatusSummary) => {
  const isEnergyMain = isRegisteredBranchMain(installerStatusSummary, BRANCH_TYPE.ENERGY);
  const isGasMain = isRegisteredBranchMain(installerStatusSummary, BRANCH_TYPE.GAS);

  return isEnergyMain || isGasMain;
};
export const hasRegisteredInstallerOneGuest = (installerStatusSummary) => {
  const isEnergyGuest = isRegisteredBranchGuest(installerStatusSummary, BRANCH_TYPE.ENERGY);
  const isGasGuest = isRegisteredBranchGuest(installerStatusSummary, BRANCH_TYPE.GAS);
  return isEnergyGuest || isGasGuest;
};
export const isRegisteringInstallerOnlyMain = (installer) => {
  const isEnergyMain =
    isInstallerRegisteringForBranch(BRANCH_TYPE.ENERGY, installer) &&
    !isRegisteringBranchGuest(BRANCH_TYPE.ENERGY, installer);
  const isGasMain =
    isInstallerRegisteringForBranch(BRANCH_TYPE.GAS, installer) &&
    !isRegisteringBranchGuest(BRANCH_TYPE.GAS, installer);

  return (
    (isEnergyMain && !isInstallerRegisteringForBranch(BRANCH_TYPE.GAS, installer)) ||
    (isGasMain && !isInstallerRegisteringForBranch(BRANCH_TYPE.ENERGY, installer)) ||
    (isEnergyMain && isGasMain)
  );
};
export const isRegisteredInstallerOnlyHilfsbetrieb = (installerStatusSummary) => {
  // check for both branches
  const hasEnergyBranch = doesRegisteredInstallerHaveBranch(
    BRANCH_TYPE.ENERGY,
    installerStatusSummary
  );
  const hasGasBranch = doesRegisteredInstallerHaveBranch(BRANCH_TYPE.GAS, installerStatusSummary);

  const isEnergyMain = isRegisteredBranchMain(installerStatusSummary, BRANCH_TYPE.ENERGY);
  const isGasMain = isRegisteredBranchMain(installerStatusSummary, BRANCH_TYPE.GAS);

  const isEnergyHilfsbetrieb = isRegisteredInstallerBranchHilfsbetrieb(
    installerStatusSummary,
    BRANCH_TYPE.ENERGY
  );
  const isGasHilfsbetrieb = isRegisteredInstallerBranchHilfsbetrieb(
    installerStatusSummary,
    BRANCH_TYPE.GAS
  );

  // Case: Both branches exist
  if (hasEnergyBranch && hasGasBranch) {
    // Sub-case: Both branches are main
    if (isEnergyMain && isGasMain) {
      // Both must be 'Hilfsbetrieb' to return true
      return isEnergyHilfsbetrieb && isGasHilfsbetrieb;
    }
    // At least one main branch must be 'Hilfsbetrieb' to return true
    return (isEnergyMain && isEnergyHilfsbetrieb) || (isGasMain && isGasHilfsbetrieb);
  }
  // Case: Only one branch exists
  // The existing branch must be 'Hilfsbetrieb' to return true
  return (hasEnergyBranch && isEnergyHilfsbetrieb) || (hasGasBranch && isGasHilfsbetrieb);
};
/**
 * Determines if a data card should be shown based on the id and installer data.
 *
 * @param {string} id - The identifier for the data card.
 * @param {Object} installerData - The installer data to check against.
 * @returns {boolean} - True if the data card should be shown, otherwise false.
 */

export const shouldShowMyDataCard = (id, installerData) => {
  switch (id) {
    case 'guest-extension':
      return hasGuestEntry(installerData);
    case 'main-extension':
      return hasMainEntry(installerData);
    case 'document-download':
      return hasDownloadEntry(installerData);
    default:
      return true;
  }
};
